<template>
  <div class="bd-line-chart-wrapper">
    <div v-if="!hasData" class="text-muted small">No data available.</div>
    <div v-else ref="chartEl" class="bd-line-chart"></div>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'

const GOOGLE_LOADER_SRC = 'https://www.gstatic.com/charts/loader.js'

function loadGoogleCharts() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('Window is undefined'))
    }

    if (window.google && window.google.charts && window.google.visualization) {
      window.google.charts.load('current', { packages: ['corechart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
      return
    }

    const existing = document.querySelector(`script[src="${GOOGLE_LOADER_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => {
        window.google.charts.load('current', { packages: ['corechart'] })
        window.google.charts.setOnLoadCallback(() => resolve(window.google))
      })
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Charts')))
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_LOADER_SRC
    script.async = true
    script.onload = () => {
      if (!window.google || !window.google.charts) {
        reject(new Error('Google Charts not available'))
        return
      }
      window.google.charts.load('current', { packages: ['corechart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
    }
    script.onerror = () => reject(new Error('Failed to load Google Charts'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'BDLineChart',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    categoryKey: {
      type: String,
      required: true,
    },
    valueKey: {
      type: String,
      required: true,
    },
    valueLabel: {
      type: String,
      default: 'Value',
    },
    // Optional multi-series configuration, e.g.
    // [{ key: 'opportunities', label: 'Opportunities' }, { key: 'contracts', label: 'Contracts' }]
    series: {
      type: Array,
      default: null,
    },
    // Custom colors: array of hex strings, e.g. ['#0d9488', '#0891b2']
    colors: {
      type: Array,
      default: null,
    },
  },
  setup(props) {
    const chartEl = ref(null)
    const chart = ref(null)
    const googleRef = ref(null)

    const hasData = computed(() => Array.isArray(props.data) && props.data.length > 0)

    const buildDataTable = () => {
      if (!googleRef.value || !hasData.value) return null
      const google = googleRef.value
      const dataTable = new google.visualization.DataTable()
      dataTable.addColumn('string', 'Category')
      const hasSeries = Array.isArray(props.series) && props.series.length > 0

      if (hasSeries) {
        props.series.forEach((s) => {
          const label = s && (s.label || s.key) ? (s.label || s.key) : 'Value'
          dataTable.addColumn('number', String(label))
        })

        const rows = props.data.map((row) => {
          const category = row[props.categoryKey] || 'Unknown'
          const seriesValues = props.series.map((s) => {
            const key = s && s.key
            const raw = key ? row[key] : 0
            const value = Number(raw ?? 0)
            return isNaN(value) ? 0 : value
          })
          return [String(category), ...seriesValues]
        })
        dataTable.addRows(rows)
      } else {
        dataTable.addColumn('number', props.valueLabel || 'Value')

        const rows = props.data.map((row) => {
          const category = row[props.categoryKey] || 'Unknown'
          const value = Number(row[props.valueKey] ?? 0)
          return [String(category), isNaN(value) ? 0 : value]
        })
        dataTable.addRows(rows)
      }
      return dataTable
    }

    const drawChart = async () => {
      if (!chartEl.value || !hasData.value) return

      try {
        if (!googleRef.value) {
          googleRef.value = await loadGoogleCharts()
        }
        const google = googleRef.value
        const dataTable = buildDataTable()
        if (!dataTable) return

        if (!chart.value) {
          chart.value = new google.visualization.LineChart(chartEl.value)
        }

        const hasSeries = Array.isArray(props.series) && props.series.length > 0

        const options = {
          legend: { position: hasSeries ? 'top' : 'none' },
          chartArea: { left: 60, top: 10, right: 10, bottom: 70 },
          hAxis: { slantedText: true, slantedTextAngle: 45, gridlines: { count: 0 } },
          vAxis: { minValue: 0, gridlines: { count: 0 } },
          lineWidth: 3,
          pointSize: 5,
          height: 320,
        }
        if (Array.isArray(props.colors) && props.colors.length > 0) {
          options.colors = props.colors
        }

        chart.value.draw(dataTable, options)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to render BD line chart', e)
      }
    }

    onMounted(() => {
      drawChart()
    })

    watch(
      () => [props.data, props.valueKey, props.categoryKey, props.colors],
      () => {
        drawChart()
      },
      { deep: true },
    )

    onBeforeUnmount(() => {
      chart.value = null
    })

    return {
      chartEl,
      hasData,
    }
  },
}
</script>

<style scoped>
.bd-line-chart-wrapper {
  width: 100%;
  min-height: 260px;
}

.bd-line-chart {
  width: 100%;
  height: 260px;
}
</style>
